/**
 * ClinicalReviewDetail
 *
 * Renders the diagnoses, medications, and allergies that will be written to a
 * patient's chart on import. Shown in the expandable detail row of the review
 * tables so clinicians can verify clinical data before approving the import.
 */

import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
} from '@mui/icons-material';
import { Box, Chip, IconButton, Stack, Typography } from '@mui/material';

import type { NewPatientItem } from '../../../types';

import type { ClinicalCounts } from './clinicalCounts';

// Derive the item shapes from the generated review item so this stays in sync
// with the API contract without depending on codegen's numbered type aliases.
type ReviewDiagnosis = NonNullable<NewPatientItem['diagnoses']>[number];
type ReviewMedication = NonNullable<NewPatientItem['medications']>[number];
type ReviewAllergy = NonNullable<NewPatientItem['allergies']>[number];

export interface ClinicalReviewDetailProps {
  diagnoses?: ReviewDiagnosis[];
  medications?: ReviewMedication[];
  allergies?: ReviewAllergy[];
}

interface ClinicalSummaryToggleProps {
  counts: ClinicalCounts;
  isOpen: boolean;
  onToggle: () => void;
}

/**
 * Compact "2 dx · 2 meds · 0 allergies" summary with an expand chevron.
 * Renders a muted dash when there is no clinical data to import.
 */
export const ClinicalSummaryToggle = ({ counts, isOpen, onToggle }: ClinicalSummaryToggleProps) => {
  if (counts.total === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {'—'}
      </Typography>
    );
  }
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      aria-expanded={isOpen}
      aria-label={`${isOpen ? 'Hide' : 'Show'} clinical data: ${counts.diagnoses} diagnoses, ${counts.medications} medications, ${counts.allergies} allergies`}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none' }}
    >
      <IconButton size="small" sx={{ p: 0.25 }} tabIndex={-1}>
        {isOpen ? (
          <KeyboardArrowDownIcon fontSize="small" />
        ) : (
          <KeyboardArrowRightIcon fontSize="small" />
        )}
      </IconButton>
      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
        {counts.diagnoses} dx · {counts.medications} meds · {counts.allergies} allergies
      </Typography>
    </Box>
  );
};

const InactiveChip = () => (
  <Chip label="inactive" size="small" variant="outlined" sx={{ height: 18 }} />
);

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="caption"
    sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.4 }}
  >
    {children}
  </Typography>
);

const EmptyText = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
    {children}
  </Typography>
);

export const ClinicalReviewDetail = ({
  diagnoses = [],
  medications = [],
  allergies = [],
}: ClinicalReviewDetailProps) => {
  return (
    <Box
      sx={{
        // Pin to the left edge and cap to the viewport so the three columns stay
        // visible inside a horizontally-scrollable table without the 1fr tracks
        // stretching the spanning cell (and the whole table) to its full width.
        position: 'sticky',
        left: 0,
        width: '100%',
        maxWidth: 'calc(100vw - 64px)',
        boxSizing: 'border-box',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
        gap: 3,
        py: 1.5,
        px: 1,
      }}
    >
      {/* Diagnoses */}
      <Box>
        <SectionHeading>Diagnoses ({diagnoses.length})</SectionHeading>
        {diagnoses.length === 0 ? (
          <EmptyText>None imported</EmptyText>
        ) : (
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {diagnoses.map((d, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">
                  {d.code ? (
                    <Box component="span" sx={{ fontWeight: 600, mr: 0.5 }}>
                      {d.code}
                    </Box>
                  ) : null}
                  {d.name}
                </Typography>
                {d.isActive === false && <InactiveChip />}
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      {/* Medications */}
      <Box>
        <SectionHeading>Medications ({medications.length})</SectionHeading>
        {medications.length === 0 ? (
          <EmptyText>None imported</EmptyText>
        ) : (
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {medications.map((m, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">
                  {m.name}
                  {m.strength ? (
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      {' '}
                      · {m.strength}
                    </Box>
                  ) : null}
                </Typography>
                {m.isActive === false && <InactiveChip />}
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      {/* Allergies */}
      <Box>
        <SectionHeading>Allergies ({allergies.length})</SectionHeading>
        {allergies.length === 0 ? (
          <EmptyText>None imported</EmptyText>
        ) : (
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {allergies.map((a, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {a.allergen}
                  </Box>
                  {a.reaction ? (
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      {' '}
                      · {a.reaction}
                    </Box>
                  ) : null}
                  {a.severity ? (
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      {' '}
                      ({a.severity})
                    </Box>
                  ) : null}
                </Typography>
                {a.isActive === false && <InactiveChip />}
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};
